import { CheckoutForm } from "@/checkout/types";
import { FormCard } from "@/forms/FormCard";
import { Button, Typography } from "@cappelendamm/design-system/react";
import {
  formatPrice,
  resolveFirstOrderPrice,
  SubscriptionProduct,
} from "@cappelendamm/e-commerce-services";
import { useFormContext, useWatch } from "react-hook-form";

type SubscriptionSummarySectionProps = {
  subscription: SubscriptionProduct;
};

export const SubscriptionSummarySection = ({
  subscription,
}: SubscriptionSummarySectionProps) => {
  const { control } = useFormContext<CheckoutForm>();
  const selectedFirstBookSku = useWatch({ control, name: "meta.initialSku" });
  const selectedGiftsList = useWatch({ control, name: "gifts" });
  const selectedGiftSections = useWatch({ control, name: "giftSections" });
  const selectedFirstBook = subscription.series?.products?.find(
    (book) => book.sku === selectedFirstBookSku,
  );
  const subscriptionCampaignBooks = subscription.campaignBooks ?? [];
  const selectedGiftsFromSingleSection =
    selectedGiftsList?.filter((gift) => gift?.selected) ?? [];
  const selectedGiftsFromSections =
    selectedGiftSections
      ?.flatMap((section) => section.options)
      .filter((gift) => gift?.selected) ?? [];
  const selectedGifts =
    selectedGiftsFromSections.length > 0
      ? selectedGiftsFromSections
      : selectedGiftsFromSingleSection;

  const orderPrice = resolveFirstOrderPrice(subscription, selectedFirstBook);
  const freightPrice = subscription.freight.initial[0]?.priceVariant.price ?? 0;

  const freightPriceString = formatPrice(freightPrice);

  const orderSumPriceString = formatPrice(orderPrice);

  const totalPriceString =
    orderPrice !== undefined ? formatPrice(orderPrice + freightPrice) : "-";

  return (
    <FormCard>
      <div className="flex items-center justify-between">
        <Typography
          as="h2"
          variant="title-lg"
        >{`Å betale ${totalPriceString}`}</Typography>
        <Button
          variant="tertiary"
          type="button"
          className="p-0!"
          onClick={() => window.scrollTo({ top: 0 })}
        >
          Se bestilling
        </Button>
      </div>
      <div>
        <div className="flex justify-between">
          <span>
            <p>Abonnement på {subscription.name}</p>
            {selectedFirstBook && subscriptionCampaignBooks.length === 0 && (
              <p className="text-sm text-gray-700">
                {`Bok ${selectedFirstBook?.numberInSeries}: "${selectedFirstBook?.name}"`}
              </p>
            )}
            {subscriptionCampaignBooks.length > 0
              ? subscriptionCampaignBooks.map((book) => (
                  <p className="text-sm text-gray-700">
                    {`Bok ${book.numberInSeries}: "${book.name}"`}
                  </p>
                ))
              : null}
            {selectedGifts.length > 0 && (
              <p className="text-sm text-gray-700">
                {`Velkomstgave${selectedGifts.length > 1 ? "r" : ""}: ${selectedGifts
                  .map((gift) => gift?.name)
                  .join(", ")}`}
              </p>
            )}
          </span>
          <p data-testid="summary-price">{orderSumPriceString}</p>
        </div>

        <div className="mt-3 flex justify-between">
          <p>Frakt</p>
          <p>{freightPriceString}</p>
        </div>
      </div>
    </FormCard>
  );
};
